import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Trash2, 
  Loader2,
  User,
  Building,
  Tag,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import customerService from '@/services/customerService';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

/**
 * İlan tipi seçenekleri
 */
const PROPERTY_TYPE_OPTIONS = [
  { id: 'satilik', label: 'Satılık' },
  { id: 'kiralik', label: 'Kiralık' },
  { id: 'villa', label: 'Villa' },
  { id: 'apartman', label: 'Apartman' },
  { id: 'arsa', label: 'Arsa' },
  { id: 'ticari', label: 'Ticari' },
];

/**
 * Oda sayısı seçenekleri
 */
const ROOM_OPTIONS = [
  { id: 'all', label: 'Tümü' },
  { id: '1+1', label: '1+1' },
  { id: '2+1', label: '2+1' },
  { id: '3+1', label: '3+1' },
  { id: '3+2', label: '3+2' },
  { id: '4+1', label: '4+1' },
  { id: '5+1', label: '5+1' },
];

/**
 * Tarih formatlama
 */
function formatDate(dateString) {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('tr-TR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/**
 * StaffCustomerFormPage - Müşteri oluşturma/düzenleme formu
 */
const StaffCustomerFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const isEditMode = !!id;
  
  // Form state
  const [formData, setFormData] = useState({
    // Kişisel bilgiler
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    lead_source: '',
    status: 'new',
    notes: '',
    
    // Bütçe aralığı
    budget_min: '',
    budget_max: '',
    
    // Lokasyon tercihi
    preferred_locations: '',
    
    // İlan tipi tercihleri
    property_types: [],
    
    // Oda sayısı tercihi
    rooms_preference: 'all',
    
    // Alan tercihi
    area_min: '',
    area_max: '',
    
    // Follow-up
    next_follow_up: '',
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (isEditMode) {
      fetchCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditMode]);

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error } = await customerService.getCustomerById(id);
      
      if (error) throw error;
      if (!data) {
        throw new Error('Müşteri bulunamadı');
      }
      
      // Form verilerini doldur
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',
        lead_source: data.lead_source || '',
        status: data.status || 'new',
        notes: data.notes || '',
        budget_min: data.budget_range?.min || '',
        budget_max: data.budget_range?.max || '',
        preferred_locations: data.preferred_locations?.join(', ') || '',
        property_types: data.property_types || [],
        rooms_preference: 'all',
        area_min: '',
        area_max: '',
        next_follow_up: data.next_follow_up ? data.next_follow_up.split('T')[0] : '',
      });
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError(err.message || 'Müşteri yüklenirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'Müşteri yüklenirken bir hata oluştu',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePropertyTypeToggle = (typeId) => {
    setFormData(prev => ({
      ...prev,
      property_types: prev.property_types.includes(typeId)
        ? prev.property_types.filter(t => t !== typeId)
        : [...prev.property_types, typeId],
    }));
  };

  const validateForm = () => {
    if (!formData.first_name?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Ad zorunludur.',
      });
      return false;
    }
    
    if (!formData.last_name?.trim()) {
      toast({
        variant: 'destructive',
        title: 'Doğrulama Hatası',
        description: 'Soyad zorunludur.',
      });
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      
      // Bütçe aralığını parse et
      const budget_range = {};
      if (formData.budget_min) budget_range.min = parseFloat(formData.budget_min);
      if (formData.budget_max) budget_range.max = parseFloat(formData.budget_max);
      
      // Lokasyonları parse et
      const preferred_locations = formData.preferred_locations
        ? formData.preferred_locations.split(',').map(l => l.trim()).filter(l => l)
        : [];
      
      // Veriyi hazırla
      const customerData = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        lead_source: formData.lead_source || null,
        status: formData.status,
        notes: formData.notes.trim() || null,
        budget_range: Object.keys(budget_range).length > 0 ? budget_range : {},
        preferred_locations,
        property_types: formData.property_types,
        next_follow_up: formData.next_follow_up ? new Date(formData.next_follow_up).toISOString() : null,
        staff_id: user?.id,
      };
      
      if (isEditMode) {
        const { error } = await customerService.updateCustomer(id, customerData);
        if (error) throw error;
        
        toast({
          title: 'Başarılı',
          description: 'Müşteri başarıyla güncellendi.',
        });
      } else {
        const { data, error } = await customerService.createCustomer(customerData);
        if (error) throw error;
        
        toast({
          title: 'Başarılı',
          description: 'Müşteri başarıyla oluşturuldu.',
        });
        
        // Yeni müşteri oluşturulduktan sonra düzenleme moduna geç
        navigate(`/staff/customers/${data.id}`, { replace: true });
        return;
      }
      
      navigate('/staff/customers');
    } catch (err) {
      console.error('Error saving customer:', err);
      setError(err.message || 'Müşteri kaydedilirken bir hata oluştu');
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: err.message || 'Müşteri kaydedilirken bir hata oluştu',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    try {
      setSaving(true);
      const { error } = await customerService.deleteCustomer(id);
      if (error) throw error;
      
      toast({
        title: 'Başarılı',
        description: 'Müşteri başarıyla silindi.',
      });
      navigate('/staff/customers');
    } catch (err) {
      console.error('Error deleting customer:', err);
      toast({
        variant: 'destructive',
        title: 'Hata',
        description: 'Müşteri silinirken bir hata oluştu.',
      });
    } finally {
      setSaving(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-muted-foreground" size={48} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Üst Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/staff/customers">
            <Button variant="outline" size="icon">
              <ArrowLeft size={18} />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              {isEditMode ? 'Müşteri Düzenle' : 'Yeni Müşteri Ekle'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {isEditMode ? 'Mevcut müşteri bilgilerini düzenleyin' : 'Yeni müşteri bilgilerini girin'}
            </p>
          </div>
        </div>
        
        {isEditMode && (
          <Button
            variant="destructive"
            onClick={() => setDeleteDialogOpen(true)}
          >
            <Trash2 size={18} className="mr-2" />
            Müşteriyi Sil
          </Button>
        )}
      </div>

      {/* Hata Mesajı */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sol Sütun - Kişisel Bilgiler */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User size={20} />
                  Kişisel Bilgiler
                </CardTitle>
                <CardDescription>Müşterinin temel iletişim bilgilerini girin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Ad */}
                <div className="space-y-2">
                  <Label htmlFor="first_name">
                    Ad <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="Örn: Ahmet"
                    required
                  />
                </div>

                {/* Soyad */}
                <div className="space-y-2">
                  <Label htmlFor="last_name">
                    Soyad <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Örn: Yılmaz"
                    required
                  />
                </div>

                {/* E-posta */}
                <div className="space-y-2">
                  <Label htmlFor="email">E-posta</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                    placeholder="ornek@email.com"
                  />
                </div>

                {/* Telefon */}
                <div className="space-y-2">
                  <Label htmlFor="phone">Telefon</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    placeholder="+90 533 123 4567"
                  />
                </div>

                {/* Lead Kaynağı */}
                <div className="space-y-2">
                  <Label htmlFor="lead_source">Lead Kaynağı</Label>
                  <Select
                    value={formData.lead_source}
                    onValueChange={(value) => handleChange('lead_source', value)}
                  >
                    <SelectTrigger id="lead_source">
                      <SelectValue placeholder="Seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="web">Web</SelectItem>
                      <SelectItem value="phone">Telefon</SelectItem>
                      <SelectItem value="social_media">Sosyal Medya</SelectItem>
                      <SelectItem value="referral">Referans</SelectItem>
                      <SelectItem value="office_visit">Ofis Ziyareti</SelectItem>
                      <SelectItem value="other">Diğer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Durum */}
                <div className="space-y-2">
                  <Label htmlFor="status">Durum</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => handleChange('status', value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">Yeni</SelectItem>
                      <SelectItem value="contacted">İletişime Geçildi</SelectItem>
                      <SelectItem value="interested">İlgileniyor</SelectItem>
                      <SelectItem value="not_interested">İlgilenmiyor</SelectItem>
                      <SelectItem value="closed">Kapatıldı</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Notlar */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Notlar</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => handleChange('notes', e.target.value)}
                    placeholder="Müşteri hakkında notlar..."
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sağ Sütun - İlan Tercihleri */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building size={20} />
                  İlan Tercihleri
                </CardTitle>
                <CardDescription>Müşterinin ilan tercihlerini belirtin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Bütçe Aralığı */}
                <div className="space-y-2">
                  <Label>Bütçe Aralığı (GBP)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="number"
                        min="0"
                        value={formData.budget_min}
                        onChange={(e) => handleChange('budget_min', e.target.value)}
                        placeholder="Min"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="0"
                        value={formData.budget_max}
                        onChange={(e) => handleChange('budget_max', e.target.value)}
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </div>

                {/* Tercih Edilen Lokasyonlar */}
                <div className="space-y-2">
                  <Label htmlFor="preferred_locations">Tercih Edilen Lokasyonlar</Label>
                  <Input
                    id="preferred_locations"
                    value={formData.preferred_locations}
                    onChange={(e) => handleChange('preferred_locations', e.target.value)}
                    placeholder="Lefkoşa, Girne, Gazimağusa..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Virgülle ayırarak birden fazla lokasyon girebilirsiniz
                  </p>
                </div>

                {/* İlan Tipleri */}
                <div className="space-y-2">
                  <Label>İlan Tipleri</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {PROPERTY_TYPE_OPTIONS.map((type) => (
                      <div key={type.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`property_type_${type.id}`}
                          checked={formData.property_types.includes(type.id)}
                          onCheckedChange={() => handlePropertyTypeToggle(type.id)}
                        />
                        <Label
                          htmlFor={`property_type_${type.id}`}
                          className="text-sm font-normal cursor-pointer"
                        >
                          {type.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Oda Sayısı Tercihi */}
                <div className="space-y-2">
                  <Label htmlFor="rooms_preference">Oda Sayısı Tercihi</Label>
                  <Select
                    value={formData.rooms_preference}
                    onValueChange={(value) => handleChange('rooms_preference', value)}
                  >
                    <SelectTrigger id="rooms_preference">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROOM_OPTIONS.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Alan Tercihi */}
                <div className="space-y-2">
                  <Label>Alan Tercihi (m²)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Input
                        type="number"
                        min="0"
                        value={formData.area_min}
                        onChange={(e) => handleChange('area_min', e.target.value)}
                        placeholder="Min"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        min="0"
                        value={formData.area_max}
                        onChange={(e) => handleChange('area_max', e.target.value)}
                        placeholder="Max"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Follow-up Takvimi */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag size={20} />
                  Follow-up Takvimi
                </CardTitle>
                <CardDescription>İletişim planlamasını takip edin</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Sonraki Follow-up Tarihi */}
                <div className="space-y-2">
                  <Label htmlFor="next_follow_up">Sonraki Follow-up Tarihi</Label>
                  <Input
                    id="next_follow_up"
                    type="date"
                    value={formData.next_follow_up}
                    onChange={(e) => handleChange('next_follow_up', e.target.value)}
                  />
                </div>

                {/* Son İletişim Tarihi (Read-only) */}
                {isEditMode && (
                  <div className="space-y-2">
                    <Label>Son İletişim Tarihi</Label>
                    <Input
                      value={formatDate(new Date().toISOString())}
                      readOnly
                      className="bg-muted"
                    />
                    <p className="text-xs text-muted-foreground">
                      Son iletişim tarihi otomatik olarak güncellenir
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Form Altı Butonlar */}
        <div className="flex items-center justify-end gap-4 mt-6 pt-6 border-t">
          <Link to="/staff/customers">
            <Button type="button" variant="outline">
              İptal
            </Button>
          </Link>
          <Button type="submit" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                Kaydediliyor...
              </>
            ) : (
              <>
                <Save size={18} className="mr-2" />
                {isEditMode ? 'Güncelle' : 'Kaydet'}
              </>
            )}
          </Button>
        </div>
      </form>

      {/* Silme Onay Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Müşteriyi Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Müşteri ve tüm aktiviteleri kalıcı olarak silinecektir.
              <br />
              <br />
              <strong>
                {formData.first_name} {formData.last_name}
              </strong>{' '}
              adlı müşteriyi silmek istediğinizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StaffCustomerFormPage;
